import { useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui";
import { useRefreshDirectory } from "@/lib/queries";
import type { DeletionPlan } from "@/lib/types";

/**
 * Le entita' che si cancellano, e dove chiedere.
 *
 * La mappa sta qui e non nei chiamanti: una pagina che costruisse da se' l'URL
 * dell'anteprima e quello della cancellazione potrebbe sbagliarne uno, e i due
 * devono parlare della stessa riga.
 */
const ROTTE = {
  cliente: (id: string) => `/clients/${id}`,
  conto: (id: string) => `/accounts/${id}`,
  fase: (id: string) => `/sessions/${id}`,
  ciclo: (id: string) => `/cycles/${id}`,
} as const;

export type DeletableKind = keyof typeof ROTTE | "simbolo";

/**
 * ELIMINA DEFINITIVAMENTE, con la conseguenza dichiarata PRIMA.
 *
 * Non «attenzione, azione irreversibile»: **la frase che dice che cosa succede a
 * questo dato**, coi numeri dentro, presa dal backend e non riscritta qui. Se la
 * frase la scrivesse l'interfaccia, il giorno in cui la regola cambia
 * l'interfaccia direbbe una cosa e il backend ne farebbe un'altra.
 *
 * Tre stati, e il primo è quello che conta:
 *
 *  1. **non si è ancora chiesto**: solo il pulsante. L'anteprima costa una
 *     lettura e una domanda al gateway — non si fa a ogni riga di una tabella,
 *     si fa quando qualcuno ci pensa davvero;
 *  2. **si è chiesto e non si può**: i motivi, uno per uno. Non «impossibile»;
 *  3. **si è chiesto e si può**: la frase, le conseguenze, e la conferma.
 *
 * È diverso da «Archivia», che sta accanto: archiviare conserva i riferimenti,
 * questa li toglie di mezzo. Serve per le righe nate per errore — e serve
 * perché archiviarle **non libererebbe lo slot unico**: un conto archiviato
 * tiene il suo numero MT5, e quel numero non si può reinserire.
 */
export function DeleteForever({
  kind,
  id,
  label,
  size,
  serverId,
  onDeleted,
}: {
  kind: DeletableKind;
  id: string;
  label: string;
  size?: "sm";
  /** Solo per `simbolo`: la mappatura vive sotto il suo server. */
  serverId?: string;
  onDeleted?: () => void;
}) {
  const refresh = useRefreshDirectory();
  const [plan, setPlan] = useState<DeletionPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const base = kind === "simbolo" ? `/servers/${serverId}/symbols/${id}` : ROTTE[kind](id);
  // Il simbolo si cancella sulla sua stessa rotta; le altre entita' hanno un
  // `/permanent` che le distingue dall'archiviazione.
  const urlCancella = kind === "simbolo" ? base : `${base}/permanent`;

  const chiedi = async () => {
    setBusy(true);
    setError(null);
    try {
      setPlan(await api.deletionPreview(`${base}/deletion-preview`));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const cancella = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteAt(urlCancella);
      setPlan(null);
      await refresh();
      onDeleted?.();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!plan) {
    return (
      <span className="inline-flex items-center gap-2">
        <Button size={size} variant="ghost" disabled={busy} onClick={() => void chiedi()}>
          {busy ? "Verifico…" : "Elimina"}
        </Button>
        {error && (
          <span role="alert" className="text-xs text-loss">
            {error}
          </span>
        )}
      </span>
    );
  }

  const bloccato = plan.blockers.length > 0;

  return (
    <div className="rounded border border-line bg-raised/40 p-3 text-left">
      <p className={`text-sm ${bloccato ? "text-ink" : "font-medium text-ink"}`}>{plan.sentence}</p>

      {bloccato ? (
        <ul className="mt-1.5 space-y-0.5">
          {plan.blockers.map((b) => (
            <li key={b.code} className="text-xs text-loss">
              {b.message}
            </li>
          ))}
        </ul>
      ) : (
        <ul className="mt-1.5 space-y-0.5">
          {plan.consequences.map((c) => (
            <li key={c.code} className="text-xs text-muted">
              {c.label}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!bloccato && (
          <Button size="sm" variant="danger" disabled={busy} onClick={() => void cancella()}>
            {busy ? "Elimino…" : `Ho capito: elimina ${label}`}
          </Button>
        )}
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setPlan(null)}>
          {bloccato ? "Chiudi" : "Annulla"}
        </Button>
      </div>

      {error && (
        <p role="alert" className="mt-2 text-xs text-loss">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * ARCHIVIA un conto. **La meta' conservativa della scelta.**
 *
 * La rotta c'era dal primo giorno e **nessun componente la chiamava**: una
 * funzione che esiste e che nessuno puo' usare, esattamente come il «Ripristina»
 * prima del passo 16.
 *
 * E da quando accanto c'e' «Elimina» e' peggio: l'operatore vedrebbe **solo
 * l'opzione distruttiva** di una coppia in cui quella conservativa esiste ma
 * passa solo dall'API. Sono le due meta' di una scelta, e mostrarne una sola la
 * fa sembrare l'unica.
 *
 * L'archiviazione non chiede un'anteprima: non c'e' niente da dichiarare —
 * niente sparisce, e si torna indietro dalla sezione «Archiviati». Il backend
 * rifiuta comunque se ci sono soldi a mercato (`marketBlocker`), e quel rifiuto
 * si mostra com'e' arrivato.
 */
export function ArchiveAccount({ id, label, size }: { id: string; label: string; size?: "sm" }) {
  const refresh = useRefreshDirectory();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const archivia = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.accounts.archive(id);
      setConfirm(false);
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {confirm ? (
        <>
          <Button size={size} variant="danger" disabled={busy} onClick={() => void archivia()}>
            {busy ? "Archivio…" : `Conferma: archivia ${label}`}
          </Button>
          <Button size={size} variant="ghost" disabled={busy} onClick={() => setConfirm(false)}>
            Annulla
          </Button>
          <span className="text-xs text-faint">Sparisce dagli elenchi, non dal database: si ripristina da Clienti.</span>
        </>
      ) : (
        <Button size={size} variant="ghost" onClick={() => setConfirm(true)}>
          Archivia
        </Button>
      )}
      {error && (
        <span role="alert" className="text-xs text-loss">
          {error}
        </span>
      )}
    </span>
  );
}
